import { useQuery } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';

/** A single audit-log row. `before`/`after`/`metadata` are arbitrary JSON returned verbatim. */
export interface AuditEvent {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorLabel: string | null;
  action: string;
  targetType: string;
  targetId: string;
  targetLabel: string | null;
  venueId: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
  metadata: unknown;
}

export interface AuditPage {
  items: AuditEvent[];
  page: number;
  pageSize: number;
  total: number;
}

/** Filters mirror the backend `GET /api/v1/franchise-administration/audit` query params. All optional. `actor` matches
 *  the actor's display name (the username shown in the table), not the actor UUID; `target`
 *  matches the target's display name (the label shown in the table), not the raw target ID. */
export interface AuditFilters {
  actor?: string;
  actions?: string[];
  targetType?: string;
  target?: string;
  venueId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

const getAudit = (filters: AuditFilters): Promise<AuditPage> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/audit', {
      params: {
        query: {
          actor: filters.actor,
          action: filters.actions,
          targetType: filters.targetType,
          target: filters.target,
          venueId: filters.venueId,
          from: filters.from,
          to: filters.to,
          page: filters.page,
          pageSize: filters.pageSize,
        },
      },
    }),
  ) as Promise<AuditPage>;

/**
 * Known audit action codes, mirroring the backend `AuditAction` enum. Used to populate
 * the action filter; human-readable labels are resolved from i18n keyed by code, the same
 * pattern the role builder uses for permission codes.
 */
export const AUDIT_ACTIONS = [
  'system.bootstrapped',
  'auth.login_success',
  'auth.login_failure',
  'auth.logout',
  'auth.session_revoked',
  'user.created',
  'user.updated',
  'user.activated',
  'user.deactivated',
  'user.password_changed_by_self',
  'user.password_reset_by_admin',
  'user.pos_pin_regenerated_by_admin',
  'user.venue_pos_pin_issued',
  'user.venue_pos_pin_regenerated',
  'user.venue_pos_pin_revoked',
  'role.created',
  'role.updated',
  'role.deleted',
  'role.permissions_changed',
  'role_assignment.created',
  'role_assignment.updated',
  'role_assignment.revoked',
  'brand.updated',
  'brand.logo_updated',
  'country_policy.created',
  'country_policy.updated',
  'country_policy.enabled_changed',
  'venue.created',
  'venue.updated',
  'venue.locale_changed',
  'venue_booking_settings.updated',
  'venue_operating_hours.updated',
  'room.created',
  'room.updated',
  'employee.created',
  'employee.updated',
  'shift.opened',
  'shift.closed',
  'shift.corrected',
  'sale.completed',
  'customer_profile.created',
  'customer_profile.updated',
  'waiver_template.created',
  'waiver_template.version_created',
  'venue.waiver_template_assigned',
  'waiver_record.signed',
  'waiver_record.revoked',
  'pass.created',
  'pass.updated',
  'pass.activated',
  'pass.deactivated',
  'mvp_definition.created',
  'mvp_definition.updated',
  'mvp_definition.activated',
  'mvp_definition.deactivated',
  'mvp_definition.deleted',
  'mvp.sold',
  'mvp_card.bound',
  'mvp_card.deactivated',
  'mvp_card.numbers_generated',
  'mvp.visit_redeemed',
  'mvp.visit_undone',
  'mvp.voided',
  'mvp.refunded',
  'standard_pass_visit.undone',
  'booking.created',
  'booking.updated',
  'booking.cancelled',
  'booking.paid',
  'booking_item_unit.redeemed',
  'booking_item_unit.cancelled',
  'booking_item_unit.refunded',
  'booking_item_unit.waiver_linked',
  'booking_item_unit.waiver_unlinked',
  'booking_item_unit.waiver_replaced',
  'inventory_item.created',
  'inventory_item.updated',
  'pos_installation.created',
  'pos_installation.enrolled',
  'pos_installation.revoked',
  'pos_device.created',
  'pos_device.updated',
  'pos_device.allocated',
  'pos_device.released',
  'pos_device.taken_over',
] as const;

/** Target-type codes that appear in audit rows; labels resolved from i18n. */
export const AUDIT_TARGET_TYPES = [
  'system',
  'auth',
  'user',
  'role',
  'role_assignment',
  'brand',
  'country_policy',
  'venue',
  'room',
  'employee',
  'shift',
  'venue_booking_settings',
  'venue_operating_hours',
  'waiver_template',
  'waiver_record',
  'customer_profile',
  'pass',
  'mvp_definition',
  'multi_visit_pass',
  'mvp_card',
  'mvp_visit',
  'standard_pass_visit',
  'booking',
  'booking_item_unit',
  'inventory_item',
  'pos_installation',
  'pos_device',
] as const;

/** Audit is list-only; the factory owns the query-key derivation (ADR 0012). The
 *  hook keeps `placeholderData` so paging holds the prior page while the next loads
 *  — an option the generic `useList` doesn't carry. */
const audit = createResource({
  key: ['audit'],
  list: (filters?: AuditFilters) => getAudit(filters ?? {}),
});

export function useAudit(filters: AuditFilters) {
  return useQuery({
    queryKey: audit.keys.list(filters),
    queryFn: () => getAudit(filters),
    placeholderData: (previous) => previous,
  });
}
